import {
  CheckCircle,
  ContactSupportOutlined,
  ErrorOutline,
  HighlightOff,
  ImageSearch,
  PendingActions,
} from "@mui/icons-material";
import { Tooltip, Theme, SxProps, Box } from "@mui/material";
import { FormSection } from "Backend";
import { OutcomeStatus, SectionValueView, ValidationStatus } from "generated/graphql";
import { useAppSelector } from "redux/hooks";
import { selectSectionValues } from "redux/slices/case";
import { selectFormSections } from "redux/slices/form.slice";
import theme from "theme";
import { P } from "theme/typography";

interface SectionOutcomeProps {
  section: FormSection;
  sectionValue: SectionValueView;
}

const emptyIcon = (sx?: SxProps<Theme>) => (
  <Box sx={{ width: 14, height: 14, borderRadius: "50%", border: `1px solid ${theme.palette.grey[100]}`, ...sx }} />
);

interface SectionValueIconProps {
  outcomeStatus: OutcomeStatus | undefined;
  sx?: SxProps<Theme>;
}

export const SectionValueIcon = ({ outcomeStatus, sx }: SectionValueIconProps) => {
  if (outcomeStatus === undefined) return emptyIcon(sx);

  const iconSx: SxProps<Theme> = {
    ...(outcomeStatus === OutcomeStatus.Valid
      ? { color: "success.main" }
      : [OutcomeStatus.Rejected, OutcomeStatus.DiscrepantInformation, OutcomeStatus.MissingInformation].includes(
            outcomeStatus
          )
        ? { color: "error.main" }
        : { color: "grey.900" }),
    ...{ fontSize: 16 },
    ...sx,
  };

  return outcomeStatus === OutcomeStatus.Valid ? (
    <CheckCircle sx={iconSx} />
  ) : outcomeStatus === OutcomeStatus.DiscrepantInformation ? (
    <ErrorOutline sx={iconSx} />
  ) : outcomeStatus === OutcomeStatus.Rejected ? (
    <HighlightOff sx={iconSx} />
  ) : outcomeStatus === OutcomeStatus.MissingInformation ? (
    <ContactSupportOutlined sx={iconSx} />
  ) : outcomeStatus === OutcomeStatus.PendingRecords ? (
    <PendingActions sx={iconSx} />
  ) : (
    emptyIcon(sx)
  );
};

interface SectionValidationIconProps {
  status: ValidationStatus | undefined;
  sx?: SxProps<Theme>;
}

export const SectionValidationIcon = ({ status, sx }: SectionValidationIconProps) => {
  if (status === undefined) return emptyIcon(sx);

  const iconSx: SxProps<Theme> = {
    ...(status === ValidationStatus.Approved
      ? { color: "success.main" }
      : status === ValidationStatus.Issue
        ? { color: "error.main" }
        : { color: "grey.900" }),
    ...{ fontSize: 16 },
    ...sx,
  };

  return status !== ValidationStatus.Unvalidated ? <ImageSearch sx={iconSx} /> : emptyIcon(sx);
};

const SectionOutcome = ({ section, sectionValue }: SectionOutcomeProps) => {
  return (
    <Tooltip title={sectionValue?.outcomeComment} arrow>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <SectionValueIcon outcomeStatus={sectionValue?.outcomeStatus} />
        <P variant="body2">{section.name}</P>
      </Box>
    </Tooltip>
  );
};

const SectionOutcomes = () => {
  const sections = useAppSelector(selectFormSections);
  const sectionValues = useAppSelector(selectSectionValues);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
      }}>
      {sections.map((s) => {
        const sectionValue = sectionValues.find((sv) => sv.sectionId === s.id);
        return s.outcomeEnabled && sectionValue ? (
          <SectionOutcome key={s.id} section={s} sectionValue={sectionValue} />
        ) : (
          <P key={s.id} variant="body4">
            {s.name}
          </P>
        );
      })}
    </Box>
  );
};

export default SectionOutcomes;
